import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'
// import 'survey-core/defaultV2.min.css'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import 'survey-core/i18n/traditional-chinese'
import { useSurveyParams } from '../context/SurveyParamsContext'
import { useProgressSaving } from '../hooks/useProgressSaving'
import {
  convertVisibleIf,
  convertTriggers,
  convertQuestionMappings,
  convertNotRequired,
  q,
  v,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const { userId, sfdSamplerId, sfdPId } = useSurveyParams()
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  const userIdRef = useRef(null)
  useEffect(() => {
    if (userId) userIdRef.current = userId
  }, [userId])
  // 需要登入嗎
  const requiresLogin = {
    login: userId ? true : false,
    title: '員工編號',
    placeholder: 'MA00000',
    alert: '員工編號有誤，請重新登入，謝謝',
  }
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = null
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 防止閃閃
  useEffect(() => {
    const timeout = setTimeout(() => {
      const isMobile = window.innerWidth <= 576
      if (!isMobile) return

      const observer = new MutationObserver((mutationsList) => {
        for (let mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const rootEl = document.querySelector('.sd-root-modern')
            if (
              rootEl &&
              !rootEl.classList.contains('sd-root-modern--mobile')
            ) {
              rootEl.classList.add('sd-root-modern--mobile')
              console.log('✅ 強制加上 mobile class 避免抖動')
            }
          }
        }
      })

      observer.observe(document.getElementById('root'), {
        childList: true,
        subtree: true,
      })

      // 清除
      return () => observer.disconnect()
    }, 100)

    return () => clearTimeout(timeout)
  }, [])

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // C14/C15 整題顯示條件：13 列只要有一列會顯示就出現；q25-q37 全選2/4則整題隱藏(避免空矩陣卡關)
  const c14c15Visible =
    '({q25}<>2 and {q25}<>3 and {q25}<>5) or ({q26}<>2 and {q26}<>3 and {q26}<>5) or ' +
    '({q27}<>2 and {q27}<>3 and {q27}<>5) or ({q28}<>2 and {q28}<>3 and {q28}<>5) or ' +
    '({q29}<>2 and {q29}<>3 and {q29}<>5) or ({q30}<>2 and {q30}<>3 and {q30}<>5) or ' +
    '({q31}<>2 and {q31}<>3 and {q31}<>5) or ({q32}<>2 and {q32}<>3 and {q32}<>5) or ' +
    '({q33}<>2 and {q33}<>3 and {q33}<>5) or ({q34}<>2 and {q34}<>3 and {q34}<>5) or ' +
    '({q35}<>2 and {q35}<>3 and {q35}<>5) or ({q36}<>2 and {q36}<>3 and {q36}<>5) or ' +
    '({q37}<>2 and {q37}<>3 and {q37}<>5)'

  // 題號為陣列實際位置(含分段標題題)，1-based，工具自動轉 0-based
  const visibleIfConditionsInit = convertVisibleIf({
    // 此版本(典通)給其他管道填寫，前三題(個資告知、S1、S2)隱藏
    q1: 'false',
    q2: 'false',
    q3: 'false',
    // C14(q38)、C15(q39) 整題顯示：至少一列方案可填才出現
    q38: c14c15Visible,
    q39: c14c15Visible,
    // B2-B7 接題：B1矩陣(q16)該困擾列若選「從未如此」(第1欄)則隱藏
    q17: '{q16.1} <> 1', // B2 困擾1 → 矩陣列1
    q18: '{q16.2} <> 1', // B3 困擾2 → 矩陣列2
    q19: '{q16.3} <> 1', // B4 困擾3 → 矩陣列3
    q20: '{q16.5} <> 1', // B5 困擾4 → 矩陣列5（列4無追問題）
    q21: '{q16.6} <> 1', // B6 困擾5 → 矩陣列6
    q22: '{q16.7} <> 1', // B7 困擾6 → 矩陣列7
    // A7(q14)選門市/店舖LINE群組(選項4) → 顯示 D1-D5(q44-q49，D4拆成D4-1/D4-2)
    q44: '{q14} anyof [4]',
    q45: '{q14} anyof [4]',
    q46: '{q14} anyof [4]',
    q47: '{q14} anyof [4]',
    q48: '{q14} anyof [4]',
    q49: '{q14} anyof [4]',
    // E1(q52)選AI工具(選項14) → 顯示 E2(q53),E3(q54)
    q53: '{q52} anyof [14]',
    q54: '{q52} anyof [14]',
    // 抽獎(q61)選我要參加抽獎(選項1) → 顯示 G姓名(q62)/電話(q63)/Mail(q64)
    q62: '{q61} = 1',
    q63: '{q61} = 1',
    q64: '{q61} = 1',
    // C應用題答(2)有經驗不想用 /(3)有經驗沒此需求 /(5)沒經驗不想用 → 隱藏 C14(q38)、C15(q39) 矩陣對應方案列
    // 應用N(C1=q25…C13=q37) 對應 矩陣第N列
    'q38.row1': '{q25} <> 2 and {q25} <> 3 and {q25} <> 5',
    'q39.row1': '{q25} <> 2 and {q25} <> 3 and {q25} <> 5',
    'q38.row2': '{q26} <> 2 and {q26} <> 3 and {q26} <> 5',
    'q39.row2': '{q26} <> 2 and {q26} <> 3 and {q26} <> 5',
    'q38.row3': '{q27} <> 2 and {q27} <> 3 and {q27} <> 5',
    'q39.row3': '{q27} <> 2 and {q27} <> 3 and {q27} <> 5',
    'q38.row4': '{q28} <> 2 and {q28} <> 3 and {q28} <> 5',
    'q39.row4': '{q28} <> 2 and {q28} <> 3 and {q28} <> 5',
    'q38.row5': '{q29} <> 2 and {q29} <> 3 and {q29} <> 5',
    'q39.row5': '{q29} <> 2 and {q29} <> 3 and {q29} <> 5',
    'q38.row6': '{q30} <> 2 and {q30} <> 3 and {q30} <> 5',
    'q39.row6': '{q30} <> 2 and {q30} <> 3 and {q30} <> 5',
    'q38.row7': '{q31} <> 2 and {q31} <> 3 and {q31} <> 5',
    'q39.row7': '{q31} <> 2 and {q31} <> 3 and {q31} <> 5',
    'q38.row8': '{q32} <> 2 and {q32} <> 3 and {q32} <> 5',
    'q39.row8': '{q32} <> 2 and {q32} <> 3 and {q32} <> 5',
    'q38.row9': '{q33} <> 2 and {q33} <> 3 and {q33} <> 5',
    'q39.row9': '{q33} <> 2 and {q33} <> 3 and {q33} <> 5',
    'q38.row10': '{q34} <> 2 and {q34} <> 3 and {q34} <> 5',
    'q39.row10': '{q34} <> 2 and {q34} <> 3 and {q34} <> 5',
    'q38.row11': '{q35} <> 2 and {q35} <> 3 and {q35} <> 5',
    'q39.row11': '{q35} <> 2 and {q35} <> 3 and {q35} <> 5',
    'q38.row12': '{q36} <> 2 and {q36} <> 3 and {q36} <> 5',
    'q39.row12': '{q36} <> 2 and {q36} <> 3 and {q36} <> 5',
    'q38.row13': '{q37} <> 2 and {q37} <> 3 and {q37} <> 5',
    'q39.row13': '{q37} <> 2 and {q37} <> 3 and {q37} <> 5',
  })

  // 分段標題題(無選項)設為非必填，題號為陣列實際位置
  const notRequired = convertNotRequired([1, 4, 7, 15, 23, 24, 43, 51, 55])

  const mappedRateChoice = null
  const pageBreakQuestions = null

  const triggersConditionsInit = convertTriggers([
    {
      type: 'complete',
      expression: `({q6} anyof [1, 7]) or ({q8} anyof [5, 6]) or ({q12} anyof ["none"]) or ({q13} anyof ["none"])`,
    },
  ])

  const questionMappings = convertQuestionMappings({})

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    requiresLogin,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    sfdSamplerId,
    sfdPId,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  useProgressSaving({
    userIdRef,
    surveyModel,
    convertedSurvey: myAgreeJson,
    requiresLogin,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    sfdSamplerId,
  })

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'instant',
        })

        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {
    // 參考 P231113152849 或 P231101165555 或 P231206105643
  }

  // 終止條件（樣本額滿）結語
  const endMessage = `目前與您相同特性的樣本已額滿，沒辦法參與本次活動。<br>感謝您撥冗填答，祝您有美好的一天！`

  // 正常完成客製結語
  const completedMessage = `感謝您熱心與我們分享您的想法，也歡迎轉傳給親朋好友填答！<br>祝您有美好的一天！<br><br>若想參加更多問卷調查抽好康，或相關活動消息，歡迎加入典通公司之社群：<br>1.請點擊下方按鈕加入EZChoice APP，或複製下載連結<a href='https://ezcapp.page.link/tAQv'>https://ezcapp.page.link/tAQv</a><br>2.請追蹤關注【輕鬆點點】粉絲專頁 <a href='https://www.facebook.com/EasyCTRs'>https://www.facebook.com/EasyCTRs</a><br><br>如有任何問題，請於週一到週五上班日10:00~18:00電話聯繫典通股份有限公司，我們會盡快回覆您的問題。<br>典通股份有限公司│02-23315133#836葉小姐`

  const endSurveyMsg = (survey) => {
    if (
      survey.getValue(q(6)) === v(1) ||
      survey.getValue(q(6)) === v(7) ||
      survey.getValue(q(8)) === v(5) ||
      survey.getValue(q(8)) === v(6) ||
      survey.getValue(q(12))?.[0] === 'none' ||
      survey.getValue(q(13))?.[0] === 'none'
    ) {
      setCompletionMessage(endMessage)
    } else {
      setCompletionMessage(completedMessage)
    }
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    requiresLogin,
    userIdRef,
    enableProgressSaving: requiresLogin.login || !!sfdSamplerId,
    sfdSamplerId,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
